import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Printer, Droplets, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

export default function Receipt() {
  const { orderId } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/payments/receipt/${orderId}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [orderId]);

  if (error) return <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">{t("order_not_found")}</div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const { txn, mill } = data;
  const planName = t(`plan_${txn.plan_id}`);
  const cycle = txn.cycle === "yearly" ? t("billing_yearly") : t("billing_monthly");
  const when = new Date(txn.updated_at || txn.created_at).toLocaleDateString();

  return (
    <div className="min-h-screen bg-secondary/40">
      <div className="no-print sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => window.close()} data-testid="receipt-back-btn"><ArrowLeft className="mr-1 h-4 w-4" /> {t("back")}</Button>
          <Button data-testid="receipt-print-btn" size="sm" onClick={() => window.print()} className="rounded-lg bg-primary hover:bg-olive-hover"><Printer className="mr-1 h-4 w-4" /> {t("download_pdf")}</Button>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-8">
        <div id="slip" data-testid="receipt-card" className="rounded-2xl border border-border bg-white p-8 text-black shadow-lg">
          <div className="flex items-center justify-between border-b border-dashed border-black/20 pb-5">
            <div className="flex items-center gap-2.5">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#3D522B] text-white"><Droplets className="h-6 w-6" /></div>
              <div>
                <p className="font-heading text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>e-olive Albania</p>
                <p className="text-xs text-black/50">{t("receipt_title")}</p>
              </div>
            </div>
            <span className="flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> {t("paid")}</span>
          </div>

          <div className="mt-5 space-y-2.5 font-mono text-sm">
            <Row label={t("mill_profile")} value={mill.mill_name} />
            <Row label={t("email")} value={mill.email} />
            <div className="my-3 border-t border-dashed border-black/20" />
            <Row label={t("order_code")} value={txn.order_id} />
            <Row label={t("plan_col")} value={planName} bold />
            <Row label={t("nav_billing")} value={cycle} />
            <Row label="Date" value={when} />
            <div className="my-3 border-t border-dashed border-black/20" />
            <div className="flex items-center justify-between text-lg font-bold">
              <span>{t("total_label")}</span>
              <span>€{Number(txn.amount).toFixed(2)}</span>
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-black/50">{t("thank_you")} · e-olive Albania © 2026</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-black/60">{label}</span>
      <span className={bold ? "font-bold" : ""}>{value}</span>
    </div>
  );
}
